/**
 * Users Management Page
 * Manage organization users, roles, and permissions
 * Accessible to Owner and Admin roles
 *
 * Updated to use new user store and components
 */

import { useEffect, useState } from 'react'
import { Users as UsersIcon, UserPlus, Send, Shield, CheckCircle, Clock } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useUserStore } from '@/store/userStore'
import { toast } from '@/store/toastStore'
import {
  UserTable,
  UserFilters,
  UserModal,
  InviteUserModal,
  RoleAssignmentModal,
  UserStatusModal,
} from '@/components/users'
import ConfirmDeleteDialog, { type DeleteMode } from '@/components/ui/ConfirmDeleteDialog'
import type { UserFormData, InviteFormData } from '@/components/users'
import type { User, UserRole } from '@/types/user.types'
import { getAssignableRoles } from '@/types/user.types'

// ============================================================================
// STATS CARD COMPONENT
// ============================================================================

function StatsCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: React.ElementType
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red'
}) {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  }

  return (
    <div className="card card-elevated p-4 hover:shadow-lg transition">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-text-secondary text-sm font-medium">{label}</p>
          <p className="text-text-primary text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function UsersPage() {
  const { user: currentUser, hasPermission } = useAuth()
  const canCreate = hasPermission('txh.user.create')
  const canUpdate = hasPermission('txh.user.update')
  const canDelete = hasPermission('txh.user.delete')

  // Store
  const {
    users,
    isLoading,
    filters,
    selectedIds,
    loadUsers,
    addUser,
    updateUser,
    deleteUser,
    activateUser,
    deactivateUser,
    inviteUser,
    assignRole,
    setFilters,
    clearFilters,
    selectAllUsers,
    clearSelection,
    getFilteredUsers,
  } = useUserStore()

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [roleChangeUser, setRoleChangeUser] = useState<User | null>(null)
  const [deleteTargetUser, setDeleteTargetUser] = useState<User | null>(null)
  const [statusTargetUser, setStatusTargetUser] = useState<User | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  // Load users on mount
  useEffect(() => {
    if (currentUser?.tenantId) {
      loadUsers(currentUser.tenantId)
    }
  }, [currentUser?.tenantId, loadUsers])

  // Get filtered users
  const filteredUsers = getFilteredUsers()

  // Calculate stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.isActive && u.isVerified).length,
    pending: users.filter(u => u.isVerified === false).length,
    byRole: {
      admin: users.filter(u => u.role === 'admin').length,
      partner: users.filter(u => u.role === 'partner').length,
      assistant: users.filter(u => u.role === 'assistant').length,
      staff: users.filter(u => u.role === 'staff').length,
      client: users.filter(u => u.role === 'client').length,
    },
  }

  // Handlers
  // Create/edit intentionally do NOT catch: UserModal surfaces the thrown error
  // inline and keeps itself open so the admin can correct the input. The success
  // toast only fires once the store action resolves.
  const handleCreateUser = async (data: UserFormData) => {
    if (!canCreate || !currentUser?.tenantId) return
    await addUser(
      {
        email: data.email,
        fullName: data.fullName,
        phone: data.phone,
        role: data.role,
        password: data.password,
      },
      currentUser.tenantId
    )
    toast.success('User added', `${data.fullName} has been created.`)
  }

  const handleEditUser = async (data: UserFormData) => {
    if (!canUpdate || !editingUser) return
    await updateUser(editingUser.userId, {
      fullName: data.fullName,
      phone: data.phone,
      isActive: data.isActive,
    })
    toast.success('Changes saved', `${data.fullName}'s profile was updated.`)
  }

  const handleDeleteUser = (user: User) => {
    if (!canDelete) return
    setDeleteTargetUser(user)
  }

  const confirmDeleteUser = async (_mode: DeleteMode, reason: string) => {
    if (!deleteTargetUser || !canDelete) return
    try {
      await deleteUser(deleteTargetUser.userId, reason)
      toast.success(
        'User moved to Recycle Bin',
        `${deleteTargetUser.fullName} can be restored by an administrator.`
      )
    } catch (err) {
      toast.error('Could not delete user', err instanceof Error ? err.message : 'Please try again.')
      throw err // re-throw so dialog shows the error
    }
  }

  const handleInviteUser = async (data: InviteFormData) => {
    if (!canCreate || !currentUser) return
    try {
      await inviteUser(
        {
          email: data.email,
          fullName: data.fullName,
          role: data.role,
          message: data.message,
        },
        currentUser.tenantId,
        currentUser.id
      )
      toast.success('Invitation sent', `An invite was sent to ${data.email}.`)
    } catch (err) {
      toast.error(
        'Could not send invitation',
        err instanceof Error ? err.message : 'Please try again.'
      )
    }
  }

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    if (!canUpdate) return
    try {
      await assignRole(userId, newRole)
      toast.success('Role updated', `The user's role is now ${newRole}.`)
    } catch (err) {
      toast.error('Could not update role', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const handleResendInvite = (user: User) => {
    // In a real app, this would resend the invitation
    toast.success('Invitation resent', `A new invite was sent to ${user.email}.`)
  }

  const openEditModal = (user: User) => {
    if (!canUpdate) return
    setEditingUser(user)
    setShowUserModal(true)
  }

  const openRoleModal = (user: User) => {
    if (!canUpdate) return
    setRoleChangeUser(user)
    setShowRoleModal(true)
  }

  const closeUserModal = () => {
    setShowUserModal(false)
    setEditingUser(null)
  }

  const closeRoleModal = () => {
    setShowRoleModal(false)
    setRoleChangeUser(null)
  }

  const handleToggleStatus = (user: User) => {
    if (!canUpdate) return
    setStatusTargetUser(user)
  }

  const confirmStatusChange = async () => {
    if (!statusTargetUser || !canUpdate) return
    const isDeactivating = statusTargetUser.isActive
    try {
      if (isDeactivating) {
        await deactivateUser(statusTargetUser.userId)
        toast.success(
          'User deactivated',
          `${statusTargetUser.fullName} has been deactivated and signed out.`
        )
      } else {
        await activateUser(statusTargetUser.userId)
        toast.success('User activated', `${statusTargetUser.fullName}'s access has been restored.`)
      }
    } catch (err) {
      toast.error(
        `Could not ${isDeactivating ? 'deactivate' : 'activate'} user`,
        err instanceof Error ? err.message : 'Please try again.'
      )
      throw err
    }
  }

  // Get available roles for assignment (can't assign owner, can only assign roles lower than own)
  const availableRoles = currentUser ? getAssignableRoles(currentUser.role) : []

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">User Management</h1>
          <p className="text-text-secondary mt-1">
            Manage your team members and their access permissions
          </p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="btn btn-outline gap-2"
            >
              <Send className="w-4 h-4" />
              Invite User
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingUser(null)
                setShowUserModal(true)
              }}
              className="btn btn-primary gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Add User
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard label="Total Users" value={stats.total} icon={UsersIcon} color="blue" />
        <StatsCard label="Active Users" value={stats.active} icon={CheckCircle} color="green" />
        <StatsCard label="Pending" value={stats.pending} icon={Clock} color="orange" />
        <StatsCard
          label="Partners & Admins"
          value={stats.byRole.admin + stats.byRole.partner}
          icon={Shield}
          color="purple"
        />
      </div>

      {/* Filters */}
      <div className="card card-elevated p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1">
            <UserFilters
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={clearFilters}
              totalCount={users.length}
              filteredCount={filteredUsers.length}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
      </div>

      {/* Users Table */}
      <UserTable
        users={filteredUsers}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onSelectionChange={canDelete ? selectAllUsers : undefined}
        onEdit={canUpdate ? openEditModal : undefined}
        onDelete={canDelete ? handleDeleteUser : undefined}
        onRoleChange={canUpdate ? openRoleModal : undefined}
        onResendInvite={canCreate ? handleResendInvite : undefined}
        onToggleStatus={canUpdate ? handleToggleStatus : undefined}
        currentUserId={currentUser?.id}
      />

      {/* Bulk Actions */}
      {canDelete && selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="card card-elevated p-4 shadow-2xl flex items-center gap-4">
            <span className="text-sm text-text-primary">
              {selectedIds.length} user{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={clearSelection} className="btn btn-ghost btn-sm">
                Clear
              </button>
              <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Delete ${selectedIds.length} selected user(s)? This cannot be undone.`
                    )
                  )
                    return
                  let successCount = 0
                  for (const id of selectedIds) {
                    try {
                      await deleteUser(id)
                      successCount++
                    } catch {
                      // continue deleting others
                    }
                  }
                  if (successCount > 0) {
                    toast.success(`${successCount} user(s) deleted`)
                    clearSelection()
                  }
                  if (successCount < selectedIds.length) {
                    toast.error(`${selectedIds.length - successCount} user(s) could not be deleted`)
                  }
                }}
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal (Create/Edit) */}
      {(editingUser ? canUpdate : canCreate) && (
        <UserModal
          isOpen={showUserModal}
          onClose={closeUserModal}
          onSubmit={editingUser ? handleEditUser : handleCreateUser}
          user={editingUser}
          mode={editingUser ? 'edit' : 'create'}
          availableRoles={availableRoles}
        />
      )}

      {/* Invite User Modal */}
      {canCreate && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteUser}
          availableRoles={availableRoles}
          tenantName={currentUser?.tenantName}
        />
      )}

      {/* Role Assignment Modal */}
      {canUpdate && (
        <RoleAssignmentModal
          isOpen={showRoleModal}
          onClose={closeRoleModal}
          onSubmit={handleRoleChange}
          user={roleChangeUser}
          availableRoles={availableRoles}
          currentUserRole={currentUser?.role}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTargetUser && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setDeleteTargetUser(null)}
          onConfirm={confirmDeleteUser}
          entityName={deleteTargetUser.fullName}
          entityType="user"
          confirmationPhrase={deleteTargetUser.email}
          confirmationLabel="email address"
          showArchiveOption={false}
          warningText={`This will deactivate ${deleteTargetUser.fullName}, revoke access, and retain the account for governed recovery.`}
        />
      )}

      {/* User Status Modal (Activate/Deactivate) */}
      {statusTargetUser && (
        <UserStatusModal
          isOpen
          onClose={() => setStatusTargetUser(null)}
          onConfirm={confirmStatusChange}
          user={statusTargetUser}
          action={statusTargetUser.isActive ? 'deactivate' : 'activate'}
        />
      )}
    </div>
  )
}
